// PRDs of a project (RF-PRD-01..06): import, versions and the coverage matrix
// requirement → tasks → tests → evidence, highlighting pending/ambiguous requirements so
// nothing disappears silently (RF-PRD-03). With the codebase analysis (D26): the completion percentage of the version,
// a "Code" column with the files behind each verdict, and the analysis panel (review, closures) above the table.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import type { ProjectSummary } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { getCoverage, listPrds } from "../../api/endpoints.ts";
import type { CoverageRow, PrdListItem, PrdVersionMeta } from "../../api/types.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { EXTRACTION_VIEW, REQUIREMENT_VIEW, statusView } from "../../lib/status.ts";
import { Button } from "../../components/Button.tsx";
import { EmptyState, ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { StatusPill } from "../../components/Status.tsx";
import { FileTextIcon, PlusIcon, UploadIcon } from "../../components/icons.tsx";
import { PrdImportDialog } from "../../features/PrdImportDialog.tsx";
import { CodebaseAnalysisPanel, CodeVerdictCell } from "../../features/onboarding/CodebaseAnalysisPanel.tsx";

/** `refreshKey` changes when the codebase analysis closed or reopened tasks: the coverage is reloaded in place. */
function CoverageTable({ versionId, refreshKey }: { versionId: string; refreshKey: number }) {
  const { t } = useI18n();
  const coverage = useAsync(() => getCoverage(versionId), [versionId]);
  const [onlyOpen, setOnlyOpen] = useState(false);
  const rows: CoverageRow[] = coverage.data?.rows ?? [];
  const completion = coverage.data?.completion;
  const attention = (r: CoverageRow) => r.requirement.state === "pending" || r.requirement.state === "ambiguous" || r.requirement.state === "not_implementable";
  const shown = onlyOpen ? rows.filter(attention) : rows;
  const openCount = rows.filter(attention).length;
  const reloadCoverage = coverage.reload;

  useEffect(() => {
    if (refreshKey > 0) reloadCoverage();
  }, [refreshKey, reloadCoverage]);

  if (coverage.loading) return <LoadingBlock label={t("common.loading")} />;
  if (coverage.error) return <ErrorBanner error={coverage.error} onRetry={coverage.reload} />;
  return (
    <div className="stack">
      {completion ? (
        <div className="row row--wrap prd-completion" data-testid="prd-completion">
          <strong>{t("onboarding.completion", { percent: completion.percent })}</strong>
          <span className="muted">{t("onboarding.completionDetail", { implemented: completion.implemented, mandatory: completion.mandatory, partial: completion.partial })}</span>
        </div>
      ) : null}
      <div className="row row--wrap row--between">
        <p className="muted">{t("prds.coverageSummary", { total: rows.length, open: openCount })}</p>
        <label className="choice">
          <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} />
          <span>{t("prds.onlyOpen")}</span>
        </label>
      </div>
      {shown.length === 0 ? (
        <p className="subtle">{rows.length === 0 ? t("prds.noRequirements") : t("prds.nothingOpen")}</p>
      ) : (
        <div className="table-wrap">
          <table className="table coverage-table">
            <caption className="sr-only">{t("prds.coverageCaption")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("prds.requirement")}</th>
                <th scope="col">{t("prds.state")}</th>
                <th scope="col">{t("prds.tasks")}</th>
                <th scope="col">{t("prds.tests")}</th>
                <th scope="col">{t("prds.evidence")}</th>
                <th scope="col">{t("onboarding.codeColumn")}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.requirement.id} className={attention(r) ? "is-highlighted" : undefined}>
                  <td>
                    <div className="stack" style={{ gap: 2 }}>
                      <span className="mono subtle">
                        {r.requirement.external_id}
                        {r.requirement.section_ref ? ` · ${r.requirement.section_ref}` : ""}
                        {r.requirement.mandatory ? "" : ` · ${t("prds.optionalReq")}`}
                      </span>
                      <span className="user-content">{r.requirement.title}</span>
                      {r.requirement.ambiguity ? (
                        <span className="field-hint">
                          {t("prds.ambiguity")}: <span className="user-content">{r.requirement.ambiguity}</span>
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <StatusPill view={REQUIREMENT_VIEW[r.requirement.state]} />
                  </td>
                  <td>
                    {r.tasks.length === 0 ? (
                      <span className="tone-warning">{t("prds.noTasks")}</span>
                    ) : (
                      <ul className="plain-list">
                        {r.tasks.map((task) => (
                          <li key={task.id}>
                            <span className="mono">#{task.number}</span> <span className="user-content">{task.title}</span>{" "}
                            <span className="subtle">({t(statusView(task.status).label)})</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td>{r.tests.length === 0 ? <span className="subtle">{t("prds.none")}</span> : r.tests.map((x) => `${x.id}: ${x.result}`).join(", ")}</td>
                  <td>{r.evidence.length === 0 ? <span className="subtle">{t("prds.none")}</span> : r.evidence.map((x) => x.kind).join(", ")}</td>
                  <td>
                    <CodeVerdictCell verdict={r.code_verdict} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Older API builds return only latest_version; newer ones also return versions[]. */
function versionsOf(prd: PrdListItem): PrdVersionMeta[] {
  if (prd.versions && prd.versions.length > 0) return [...prd.versions].sort((a, b) => b.version - a.version);
  return prd.latest_version ? [prd.latest_version] : [];
}

export function PrdsTab({ project, onChanged }: { project: ProjectSummary; onChanged: () => void }) {
  const { t, formatDateTime } = useI18n();
  const prds = useAsync(() => listPrds(project.id), [project.id]);
  const [importing, setImporting] = useState<{ prd: { id: string; title: string } | null } | null>(null);
  const [selected, setSelected] = useState<Record<string, string>>({});
  const list = useMemo(() => prds.data?.items ?? [], [prds.data]);
  const [coverageKey, setCoverageKey] = useState(0);
  const analysisChanged = () => {
    setCoverageKey((n) => n + 1);
    onChanged();
  };

  useEffect(() => {
    setSelected((cur) => {
      const next = { ...cur };
      for (const p of list) {
        const first = versionsOf(p)[0];
        if (!next[p.id] && first) next[p.id] = first.id;
      }
      return next;
    });
  }, [list]);

  return (
    <div className="stack">
      <div className="row row--wrap row--between">
        <p className="muted">{t("prds.intro")}</p>
        <Button variant="primary" icon={<UploadIcon size={16} />} onClick={() => setImporting({ prd: null })}>
          {t("prds.import")}
        </Button>
      </div>
      {prds.loading ? (
        <LoadingBlock label={t("common.loading")} />
      ) : prds.error ? (
        <ErrorBanner error={prds.error} onRetry={prds.reload} />
      ) : list.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<FileTextIcon />}
            title={t("prds.emptyTitle")}
            actions={
              <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => setImporting({ prd: null })}>
                {t("prds.import")}
              </Button>
            }
          >
            <p>{t("prds.emptyBody")}</p>
          </EmptyState>
        </div>
      ) : (
        list.map((prd) => {
          const versionId = selected[prd.id] ?? versionsOf(prd)[0]?.id ?? "";
          const version = versionsOf(prd).find((v) => v.id === versionId);
          return (
            <section key={prd.id} className="card" aria-labelledby={`prd-${prd.id}`}>
              <div className="card-header prd-header">
                <FileTextIcon size={18} />
                <h2 id={`prd-${prd.id}`} className="card-title user-content" style={{ flex: 1, minWidth: 0 }}>
                  {prd.title}
                </h2>
                {versionsOf(prd).length > 0 ? (
                  <label className="compact-select">
                    <span>{t("prds.version")}</span>
                    <select className="select" value={versionId} onChange={(e) => setSelected((s) => ({ ...s, [prd.id]: e.target.value }))}>
                      {versionsOf(prd).map((v) => (
                        <option key={v.id} value={v.id}>
                          {t("prds.versionOption", { n: v.version, date: formatDateTime(v.created_at) })}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <Button icon={<UploadIcon size={16} />} onClick={() => setImporting({ prd: { id: prd.id, title: prd.title } })}>
                  {t("prds.newVersion")}
                </Button>
              </div>
              <div className="card-body stack">
                {version ? (
                  <>
                    <div className="row row--wrap">
                      <StatusPill view={EXTRACTION_VIEW[version.extraction_state] ?? EXTRACTION_VIEW["partial"]!} />
                      {version.requirement_count !== undefined && version.requirement_count !== null ? (
                        <span className="muted">{t("prdImport.counts", { requirements: version.requirement_count, tasks: version.task_count ?? 0, pending: version.pending_count ?? 0 })}</span>
                      ) : null}
                      <Link to={`/projects/${project.id}/kanban`} className="link-subtle">
                        {t("overview.openKanban")}
                      </Link>
                    </div>
                    {/* Keyed by version: refusals, errors and confirmations never carry over to another version. */}
                    <CodebaseAnalysisPanel key={version.id} project={project} prdVersionId={version.id} onChanged={analysisChanged} />
                    <CoverageTable versionId={version.id} refreshKey={coverageKey} />
                  </>
                ) : (
                  <p className="subtle">{t("prds.noVersions")}</p>
                )}
              </div>
            </section>
          );
        })
      )}
      <PrdImportDialog
        open={importing !== null}
        projectId={project.id}
        prd={importing?.prd ?? null}
        onClose={() => setImporting(null)}
        onImported={() => {
          prds.reload();
          onChanged();
        }}
      />
    </div>
  );
}
